import React from 'react';
import { NumericFormat } from 'react-number-format';
import { Box, FormControl, FormHelperText, Input, InputLabel, InputProps, TextField, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { currencyDecimalSeperator, currencyPrefix, currencyThousandSeperator, formInputDate } from '/src/lib/constants';
import { isNumber } from '/src/utils';

export type FuseInputProps = {
  /**
   * Input label
   */
  label: string;
  /**
   * Updates the input value
   * @param value Given value
   * @returns void
   */
  updateValue: (value: any) => void;
  /**
   * Input value
   */
  value?: any;
  /**
   * Input type
   */
  type?: InputProps['type'];
  /**
   * Data field associated with this input
   */
  field?: string;
  /**
   * The enclosing form's values
   */
  formValues?: any;
  /**
   * Element ID
   */
  id?: string;
  /**
   * Minimum value
   */
  min?: string;
  /**
   * Maximum value
   */
  max?: string;
  /**
   * Value's minimum character length
   */
  minLength?: number;
  /**
   * Value's maximum character length
   */
  maxLength?: number;
  /**
   * Whether this input is required
   */
  required?: boolean;
  /**
   * Tooltip to show on hover
   */
  tooltip?: string;
  /**
   * Input focused status
   */
  focused?: boolean;
  /**
   * Displays value as a percentage
   */
  percentageMask?: boolean;
  /**
   * Whether this value is an int or float
   */
  isDecimalPercent?: boolean;
  /**
   * Displays value as a dollar amount
   */
  moneyMask?: boolean;
  /**
   * Whether the value is a negative number
   */
  negativeNumber?: boolean;
  /**
   * Error to display if `required` is set to true
   */
  error?: string;
  /**
   * Whether this component is disabled
   */
  disabled?: boolean;
  /**
   * Text to display if no value is set
   */
  placeholder?: string;
  /**
   * Disables the component's margin
   */
  disableMargin?: boolean;
  /**
   * Called when enter key is pressed
   * @returns void
   */
  onPressEnter?: () => void;
};

export const FuseInput: React.FC<FuseInputProps> = ({
  label,
  updateValue,
  value: _value,
  type,
  id,
  field,
  formValues,
  required,
  disabled,
  tooltip,
  focused,
  percentageMask,
  isDecimalPercent = true, // assumes the value given is between 0 and 1
  moneyMask,
  negativeNumber,
  error,
  minLength,
  maxLength,
  placeholder,
  disableMargin,
  onPressEnter,
  ...inputProps
}) => {
  const value = _value ?? formValues?.[field ?? ''] ?? '';
  const isDate = type === 'date';
  const formLabel = (
    <InputLabel id={id ? `${id}-label` : `${label.toLocaleLowerCase()}-input-label`}>{label}</InputLabel>
  );

  const renderCurrencyInput = () => (
    <NumericFormat
      fullWidth
      customInput={Input}
      thousandSeparator={currencyThousandSeperator}
      decimalSeparator={currencyDecimalSeperator}
      prefix={currencyPrefix}
      allowNegative={negativeNumber}
      decimalScale={2}
      fixedDecimalScale
      id={id}
      value={value}
      onValueChange={(values) => {
        const newVal = values.value.trim();
        if (isNumber(newVal)) updateValue(parseFloat(newVal));
      }}
      required={required}
      disabled={disabled}
      inputProps={{
        minLength,
        maxLength,
        ...inputProps,
      }}
      placeholder={placeholder}
      onKeyDown={(e) => {
        if (onPressEnter && e.key === 'Enter') onPressEnter();
      }}
    />
  );

  const renderPercentageInput = () => (
    <NumericFormat
      fullWidth
      customInput={Input}
      suffix="%"
      decimalScale={2}
      fixedDecimalScale
      allowNegative={negativeNumber}
      id={id}
      value={isDecimalPercent && isNumber(value) ? value * 100 : value}
      onValueChange={(values) =>
        updateValue(isDecimalPercent && isNumber(values.value) ? parseFloat(values.value) / 100 : values.value)
      }
      required={required}
      disabled={disabled}
      inputProps={{
        minLength,
        maxLength,
        ...inputProps,
      }}
      placeholder={placeholder}
      onKeyDown={(e) => {
        if (onPressEnter && e.key === 'Enter') onPressEnter();
      }}
    />
  );

  const renderTextInput = () => (
    <Input
      fullWidth
      id={id}
      type={type}
      value={value}
      onChange={(e) => updateValue(e.target.value)}
      required={required}
      disabled={disabled}
      inputProps={{
        minLength,
        maxLength,
        ...inputProps,
      }}
      placeholder={placeholder}
      onKeyDown={(e) => {
        if (onPressEnter && e.key === 'Enter') onPressEnter();
      }}
    />
  );

  const renderDateInput = () => (
    <TextField
      fullWidth
      id={id}
      type="date"
      label={
        tooltip ? (
          <Tooltip title={tooltip}>
            <Box>{label}</Box>
          </Tooltip>
        ) : (
          label
        )
      }
      value={value ? dayjs(value).format(formInputDate) : ' '}
      onChange={(e) => updateValue(dayjs(e.target.value).toDate())}
      required={required}
      disabled={disabled}
      variant="standard"
      slotProps={{
        inputLabel: {
          shrink: true,
        },
      }}
      // inputProps={{
      //   ...inputProps,
      // }}
      placeholder={placeholder}
      onKeyDown={(e) => {
        if (onPressEnter && e.key === 'Enter') onPressEnter();
      }}
    />
  );

  const renderInput = () => {
    if (percentageMask) {
      return renderPercentageInput();
    } else if (moneyMask) {
      return renderCurrencyInput();
    } else if (isDate) {
      return renderDateInput();
    } else return renderTextInput();
  };

  return (
    <FormControl
      fullWidth
      variant="standard"
      error={!!error}
      focused={focused}
      sx={!disableMargin ? { margin: '20px 0 26px 0' } : {}}
    >
      {!isDate && (tooltip ? <Tooltip title={tooltip}>{formLabel}</Tooltip> : formLabel)}
      {renderInput()}
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};
